/// <amd-module name="Repos/CommitsList/Components/CommitsTable" />
import * as React from "react";
import { ICommitsDataProviderService } from "Repos/CommitsList/CommitsDataProviderService";
import * as Resources from "Repos/CommitsList/Resources";
import { GitPullRequest, GitStatus } from "Repos/Common/Contracts/Git";
import { ICommit, ICommitsTableProps, ICommitTagRef } from "Repos/Common/Contracts/History";
import { IReposRoutingService } from "Repos/Common/ReposRoutingService";
import { getChangeTypeDisplayName } from "Repos/Common/Util/ChangeType";
import { getShortCommitComment } from "Repos/Common/Util/Commit";
import { tagToVersionDescriptor } from "Repos/Common/Util/Version";
import { getGitRepository } from "Repos/Common/VCViewModelDataProviderService";
import { PipelineStatus } from "Repos/PipelineStatus/Components/PipelineStatus";
import { getTFSProject } from "Tfs/Platform/Page";
import { IObservableValue, ObservableLike, ObservableValue } from "VSS/Core/Observable";
import { ScreenBreakpoints } from "VSS/Core/Util/Screen";
import { dateToString, format } from "VSS/Core/Util/String";
import { onClickFPS } from "VSS/Platform/FPS";
import { Components, css, IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { Ago } from "VSSUI/Ago";
import { Observer } from "VSSUI/Observer";
import { IBasicIdentityRef, Persona, PersonaSize } from "VSSUI/Persona";
import { Pill, PillSize } from "VSSUI/Pill";
import { PillGroup, PillGroupOverflow } from "VSSUI/PillGroup";
import {
    ColumnMore,
    ITableBreakpoint,
    ITableColumn,
    ITableRow,
    ITableRowDetails,
    SimpleTableCell,
    Table,
    TableColumnLayout,
    TableLoadingRow,
    TwoLineTableCell
} from "VSSUI/Table";
import { Tooltip } from "VSSUI/TooltipEx";
import { AgoFormat } from "VSSUI/Utilities/Date";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";
import { CommitsPullRequestLink } from "./CommitsPullRequestLink";
import { IHistoryGraph, IHistoryGraphRow } from "./HistoryGraphCell.Props";
import { HistoryGraphRow } from "./HistoryGraphRow";

export interface ICommitsTableWithGraphProps extends ICommitsTableProps {
    hideGraphDrawing?: boolean;
    historyGraph?: IHistoryGraph;
    isGraphReady?: IObservableValue<boolean>;
    showGraph?: boolean;
}

export class CommitsTable extends VssComponent<ICommitsTableWithGraphProps> {
    private commitsDataService: ICommitsDataProviderService;
    private graphWidth: ObservableValue<number> = new ObservableValue(125);
    private columnMore: ColumnMore<ICommit>;
    private readonly tableRef = React.createRef<Table<ICommit>>();

    private graphColumn: ITableColumn<ICommit> = {
        columnLayout: TableColumnLayout.none,
        id: "graphColumn",
        renderCell: (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<ICommit>, tableItem: ICommit) => {
            return (
                <SimpleTableCell
                    columnIndex={columnIndex}
                    key={columnIndex}
                    tableColumn={tableColumn}
                    className="flex flex-center"
                    contentClassName="repos-graph"
                >
                    <Observer
                        row={this.props.historyGraph ? this.props.historyGraph.rows[tableItem.commit.commitId] : undefined}
                        graphWidth={this.graphWidth}
                        isGraphReady={this.props.isGraphReady}
                    >
                        {(props: { row: IHistoryGraphRow | undefined; graphWidth: number; isGraphReady: boolean | undefined}) => {
                            return (
                                <div
                                    className={css((props.graphWidth === 0 || this.props.hideGraphDrawing) && "invisible")}
                                    onClick={(event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
                                        if (this.props.historyGraph) {
                                            this.props.historyGraph.select(tableItem.commit.commitId);
                                            event.preventDefault();
                                        }
                                    }}
                                >
                                    <HistoryGraphRow
                                        commit={tableItem}
                                        staticHeight={50}
                                        staticWidth={16}
                                        row={props.isGraphReady ? props.row : undefined}
                                    />
                                </div>
                            );
                        }}
                    </Observer>
                </SimpleTableCell>
            );
        },
        name: Resources.GraphHeader,
        width: this.graphWidth
    };

    private commentColumn: ITableColumn<ICommit> = {
        columnLayout: TableColumnLayout.twoLine,
        id: "comment",
        renderCell: (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<ICommit>, tableItem: ICommit) => {
            const shortComment = getShortCommitComment(tableItem.comment, 250);

            const truncatedComment = renderTooltipWithBreaks(
                tableItem.commit.commentTruncated && tableItem.comment ? tableItem.comment.trim() + "..." : tableItem.comment
            );

            const line1 = (
                <div className="text-ellipsis font-weight-semibold flex-row flex-grow flex-wrap">
                    <Tooltip overflowOnly={shortComment === tableItem.comment} renderContent={truncatedComment ? () => truncatedComment : undefined}>
                        <div className="commit-title text-ellipsis font-weight-semibold flex-grow">{shortComment}</div>
                    </Tooltip>
                    <Observer tags={tableItem.tags}>
                        {(props: { tags: ICommitTagRef[] | undefined }) => {
                            return props.tags && props.tags.length && this.props.showCommitDetails ? (
                                <PillGroup
                                    className="repos-commits-table-pills flex-noshrink margin-left-8 font-weight-normal"
                                    overflow={PillGroupOverflow.fade}
                                >
                                    {props.tags.map(tag => (
                                        <Tooltip
                                            key={tag.name}
                                            renderContent={() => (
                                                <div>
                                                    <div className="font-weight-semibold">{tag.name}</div>
                                                    <div>{tag.comment}</div>
                                                    {tag.tagger && (
                                                        <div>
                                                            {format(
                                                                Resources.CommitTagAuthorFormat,
                                                                tag.tagger.name,
                                                                dateToString(tag.tagger.date, true)
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        >
                                            <span>
                                                <Pill
                                                    excludeTabStop={true}
                                                    iconProps={{ className: "icon-margin", iconName: "Tag" }}
                                                    onClick={event => {
                                                        const url = this.context.pageContext
                                                            .getService<IReposRoutingService>("IReposRoutingService")
                                                            .getFilesUrl(
                                                                getGitRepository(this.context.pageContext)!.name,
                                                                getTFSProject(this.context.pageContext)!.name,
                                                                undefined,
                                                                tagToVersionDescriptor(tag.name)
                                                            );
                                                        onClickFPS(this.context.pageContext, url, true, event);
                                                    }}
                                                    size={PillSize.compact}
                                                >
                                                    {tag.name}
                                                </Pill>
                                            </span>
                                        </Tooltip>
                                    ))}
                                </PillGroup>
                            ) : null;
                        }}
                    </Observer>
                </div>
            );

            const personaRef: IBasicIdentityRef = {
                displayName: tableItem.commit.author.name,
                imageUrl: tableItem.commit.author.imageUrl,
                id: tableItem.commit.author.email
            };

            const line2 = (
                <div className="flex-row flex-center body-s secondary-text rhythm-horizontal-8 scroll-hidden flex-wrap">
                    <div className="flex-row flex-center scroll-hidden">
                        <Tooltip overflowOnly={true}>
                            <span className="text-ellipsis monospaced-xs">{tableItem.shortCommitId}</span>
                        </Tooltip>
                    </div>
                    <div className="flex-row flex-center scroll-hidden rhythm-horizontal-4">
                        <Persona identity={personaRef} size={PersonaSize.size16} />
                        <Tooltip overflowOnly={true}>
                            <div className="text-ellipsis">{tableItem.commit.author.name}</div>
                        </Tooltip>
                    </div>
                    <div className="flex-row flex-center scroll-hidden">
                        <Ago date={tableItem.commit.author.date} format={AgoFormat.Extended} />
                    </div>
                </div>
            );

            return (
                <TwoLineTableCell
                    className="repos-commits-table-content"
                    columnIndex={columnIndex}
                    key={columnIndex}
                    tableColumn={tableColumn}
                    line1={line1}
                    line2={line2}
                />
            );
        },
        minWidth: 200,
        name: Resources.CommitHeader,
        width: new ObservableValue(-100)
    };

    constructor(props: ICommitsTableProps, context: IVssComponentContext) {
        super(props, context);
        this.commitsDataService = context.pageContext.getService<ICommitsDataProviderService>("ICommitsDataProviderService");

        this.columnMore = new ColumnMore((item, rowIndex, buttonRef) => {
            return {
                id: "commits-more-action-menu",
                items: this.commitsDataService.getCommitMenuItems(item, async () => {
                    // a11y: Restore focus to the more-button after we dismiss any dialog (create branch, create tag)
                    // First we need to ensure row is focused. This is because while loading a dialog async'ly,
                    // it uses to lose focus, so button more gets hidden
                    await this.tableRef.current?.focusRow(rowIndex);
                    buttonRef.current?.focus();
                })
            };
        });
    }

    public render() {
        const showingGraph = this.props.showGraph && this.props.historyGraph;
        const onRowSelect = showingGraph
            ? (event: React.SyntheticEvent<HTMLElement>, tableRow: ITableRow<ICommit>) => {
                  this.props.historyGraph && this.props.historyGraph.select(tableRow.data.commit.commitId);
              }
            : undefined;

        // Build up the columns needed for the table.
        const tableColumns: ITableColumn<ICommit>[] = [];
        if (this.props.showGraph) {
            tableColumns.push(this.graphColumn);
        }
        tableColumns.push(this.commentColumn);
        if (this.props.showChangeType) {
            tableColumns.push(changeTypeColumn);
        }
        if (this.props.showCommitDetails) {
            tableColumns.push(pullRequestColumn, statusColumn);
        }
        tableColumns.push(this.columnMore);

        let tableBreakpoints: ITableBreakpoint[] | undefined;

        // If we have more than the 2 default columns we apply the tables breakpoints.
        if (tableColumns.length > 2) {
            const tableBreakpointWidths = tableColumns.map((x, index) => {
                if ((this.props.showGraph && index === 1) || (!this.props.showGraph && index === 0)) {
                    return -100;
                }
                return 0;
            });

            tableBreakpoints = [
                {
                    breakpoint: ScreenBreakpoints.xsmall,
                    columnWidths: tableBreakpointWidths
                },
                {
                    breakpoint: ScreenBreakpoints.small,
                    columnWidths: tableColumns.map(column => ObservableLike.getValue(column.width))
                }
            ];
        }

        return (
            <Table<ICommit>
                ariaLabel={Resources.CommitsTable}
                className={css(this.props.className, "repos-commits-table", showingGraph && "with-graph")}
                columns={tableColumns}
                itemProvider={new ArrayItemProvider(this.props.commits)}
                onActivate={(event: React.SyntheticEvent<HTMLElement>, tableRow: ITableRow<ICommit>) => {
                    if (this.props.onActivate) {
                        this.props.onActivate(event, tableRow.data);
                    }
                    if (!event.defaultPrevented) {
                        onClickFPS(this.context.pageContext, tableRow.data.linkProps.href!, true, event);
                    }
                }}
                onSelect={onRowSelect}
                ref={this.tableRef}
                selectableText={true}
                showHeader={!!this.props.showHeader}
                spacerWidth={this.props.spacerWidth}
                renderLoadingRow={(rowIndex: number, rowDetails: ITableRowDetails<ICommit>) => {
                    return (
                        <Observer dataLoaded={this.props.dataLoaded}>
                            {(props: { dataLoaded: boolean | undefined }) => {
                                props.dataLoaded && this.props.loadMore && this.props.loadMore();
                                return <TableLoadingRow columns={tableColumns} details={rowDetails} rowIndex={rowIndex} />;
                            }}
                        </Observer>
                    );
                }}
                tableBreakpoints={tableBreakpoints}
            />
        );
    }
}

Components.add("repos-commits-table", CommitsTable);

const changeTypeColumn: ITableColumn<ICommit> = {
    id: "changeType",
    renderCell: (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<ICommit>, tableItem: ICommit) => {
        return (
            <SimpleTableCell columnIndex={columnIndex} key={columnIndex} tableColumn={tableColumn}>
                {getChangeTypeDisplayName(tableItem.changeType)}
            </SimpleTableCell>
        );
    },
    name: Resources.ChangeTypeHeader,
    width: new ObservableValue(100)
};

const pullRequestColumn: ITableColumn<ICommit> = {
    className: "repos-commits-table-prs",
    id: "pullRequestColumn",
    renderCell: (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<ICommit>, tableItem: ICommit) => {
        return (
            <SimpleTableCell columnIndex={columnIndex} key={columnIndex} tableColumn={tableColumn}>
                <Observer pullRequest={tableItem.pullRequest}>
                    {(props: { pullRequest: GitPullRequest | undefined }) => {
                        return props.pullRequest ? (
                            <CommitsPullRequestLink
                                pullRequest={props.pullRequest}
                                projectName={tableItem.projectName || ""}
                                repositoryName={tableItem.repositoryName || ""}
                            />
                        ) : (
                            <div />
                        );
                    }}
                </Observer>
            </SimpleTableCell>
        );
    },
    name: Resources.PullRequestHeader,
    width: new ObservableValue(120)
};

const statusColumn: ITableColumn<ICommit> = {
    id: "statusColumn",
    renderCell: (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<ICommit>, tableItem: ICommit) => {
        return (
            <SimpleTableCell columnIndex={columnIndex} key={columnIndex} tableColumn={tableColumn}>
                <Observer statuses={tableItem.statuses}>
                    {(props: { statuses: GitStatus[] | undefined }) => {
                        return <PipelineStatus statuses={props.statuses} />;
                    }}
                </Observer>
            </SimpleTableCell>
        );
    },
    name: Resources.StatusHeader,
    width: new ObservableValue(170)
};

function renderTooltipWithBreaks(text?: string) {
    const result = text && (
        <div>
            {text.split("\n").map((str, index) => (
                <p key={index}>{str}</p>
            ))}
        </div>
    );
    return result;
}
